import { cn } from '~/utils/cn';

import { STEP_NAME, type StepKey } from './post-step-journey-utils';

export interface StepperItem {
  key: StepKey;
  /** "N to fix" / "All good" / "Awaiting approval". */
  sub: string;
  /** Red when something needs fixing, green when all good, neutral otherwise. */
  tone: 'bad' | 'ok' | 'neutral';
  /** The batch did not run this step: shown greyed out, not selectable. */
  disabled?: boolean;
}

const SUB_TONE = {
  bad: 'text-error',
  ok: 'text-emerald-700',
  neutral: 'text-muted-foreground',
} as const;

/** The journey's steps, always all four at the top of the Runs section; steps not run are greyed. */
export function PostStepStepper({
  steps,
  active,
  onSelect,
}: {
  steps: StepperItem[];
  active: StepKey;
  onSelect: (step: StepKey) => void;
}) {
  return (
    <ol
      aria-label="Steps"
      data-testid="journey-stepper"
      className="bg-muted/40 grid grid-cols-1 gap-1 rounded-xl border p-1 sm:grid-cols-2 lg:grid-cols-4"
    >
      {steps.map((s, i) => {
        const isActive = s.key === active;
        return (
          <li key={s.key} className="flex">
            <button
              type="button"
              data-testid={`journey-step-${s.key.toLowerCase()}`}
              aria-current={isActive ? 'step' : undefined}
              disabled={s.disabled}
              onClick={() => {
                onSelect(s.key);
              }}
              className={cn(
                'flex min-h-14 flex-1 items-center gap-3 rounded-lg px-3.5 py-2.5 text-left transition-colors',
                isActive && 'bg-primary text-white',
                !isActive && !s.disabled && 'hover:bg-muted cursor-pointer',
                s.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                  isActive ? 'text-primary bg-white' : 'bg-muted text-foreground',
                )}
              >
                {i + 1}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold">{STEP_NAME[s.key]}</span>
                <span
                  data-testid={`journey-step-sub-${s.key.toLowerCase()}`}
                  className={cn(
                    'text-xs font-medium',
                    isActive ? 'text-white/80' : SUB_TONE[s.tone],
                  )}
                >
                  {s.sub}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
